import { useEffect, useState, useRef } from "react";
import { usePageTitle } from "../../../../contexts/PageTitleContext";
import { useError } from "../../../../contexts/ErrorContext";
import getErrorMessage from "../../../../utils/errorMessages";
import { fetchClients, createClient } from "../../../../api/client";
import styles from "./Clients.module.scss";
import ClientTable from "./ClientTable/ClientTable";
import Button from "../../../../components/Button/Button";
import InputCustom from "../../../../components/InputCustom/InputCustom";
import { useFormik } from "formik";
import validationSchema from "../../../../models/ClientSchema";
import { toast } from "react-toastify";

import { fetchAddressSuggestions } from "../../../../api/address";

function debounce(func, delay) {
  let timer;
  return function (...args) {
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(context, args), delay);
  };
}

function Clients() {
  const { setPageTitle } = usePageTitle();
  const { setErrorMessage } = useError();

  const [clients, setClients] = useState([]);
  const [creatingClient, setCreatingClient] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressRef = useRef();

  useEffect(() => {
    setPageTitle("Clients");
  }, [setPageTitle]);

  useEffect(() => {
    const getClients = async () => {
      try {
        const response = await fetchClients();
        setClients(response.data);
      } catch (error) {
        const errorMessage = getErrorMessage(error.response?.status, "Clients");
        setErrorMessage(errorMessage);
      }
    };

    getClients();
  }, [setErrorMessage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressRef.current && !addressRef.current.contains(event.target)) {
        setAddressSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  const fetchAddressSuggestionsDebouncedRef = useRef(
    debounce(async (inputText) => {
      try {
        const suggestions = await fetchAddressSuggestions(inputText);
        setAddressSuggestions(suggestions);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des suggestions d'adresse : ",
          error,
        );
        setErrorMessage(
          "Erreur lors de la récupération des suggestions d'adresse.",
        );
      }
    }, 500),
  );

  const selectAddress = (address) => {
    formik.setFieldValue("address", address);
    setAddressSuggestions([]);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      address: "",
      email: "",
      phoneNumber: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await createClient(values);
        setClients((prev) => [...prev, response.data]);
        toast.success("Client créé avec succès.");

        formik.resetForm();
        setCreatingClient(false);
      } catch (error) {
        console.error("Erreur lors de la création du client :", error);
        const raw = error.response?.data?.message || "";
        if (raw.includes("Duplicate entry")) {
          toast.error("Un client avec cet email existe déjà.");
        } else {
          toast.error(raw || "Une erreur est survenue.");
        }
      }
    },
  });

  const handleCreateNewClient = () => {
    setCreatingClient(true);
  };

  const handleCancelCreatingClient = () => {
    setCreatingClient(false);
    formik.resetForm();
    setAddressSuggestions([]);
  };

  return (
    <div className={styles.container}>
      {creatingClient ? (
        <div className={styles.creationClientContainer}>
          <h2>Création d'un client</h2>

          <form onSubmit={formik.handleSubmit}>
            <div className={styles.row}>
              <div className={styles.lastName}>
                <h3>Nom</h3>
                <InputCustom
                  id="lastName"
                  type="text"
                  name="lastName"
                  placeholder="Nom"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                  style={{ marginTop: "10px" }}
                />
                {formik.touched.lastName && formik.errors.lastName ? (
                  <div className={styles.error}>{formik.errors.lastName}</div>
                ) : null}
              </div>

              <div className={styles.firstName}>
                <h3>Prénom</h3>
                <InputCustom
                  id="firstName"
                  type="text"
                  name="firstName"
                  placeholder="Prénom"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  style={{ marginTop: "10px" }}
                />
                {formik.touched.firstName && formik.errors.firstName ? (
                  <div className={styles.error}>{formik.errors.firstName}</div>
                ) : null}
              </div>
            </div>

            <div className={styles.autocompleteContainer} ref={addressRef}>
              <h3>Adresse</h3>
              <InputCustom
                id="address"
                type="text"
                name="address"
                placeholder="3 rue de l'exemple, 59300 Valenciennes"
                onChange={(e) => {
                  const inputValue = e.target.value;
                  formik.handleChange(e);
                  if (inputValue.length >= 6) {
                    fetchAddressSuggestionsDebouncedRef.current(inputValue);
                  } else {
                    setAddressSuggestions([]);
                  }
                }}
                value={formik.values.address}
                style={{ marginTop: "20px" }}
              />
              {formik.touched.address && formik.errors.address ? (
                <div className={styles.error}>{formik.errors.address}</div>
              ) : null}
              {addressSuggestions.length > 0 && (
                <div className={styles.dropdownMenu}>
                  {addressSuggestions.map((suggestion) => (
                    <div
                      key={suggestion.osm_id}
                      className={styles.dropdownItem}
                      onClick={() => selectAddress(suggestion.address)}
                    >
                      {suggestion.address}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.mailContainer}>
              <h3>Email</h3>
              <InputCustom
                id="email"
                type="text"
                name="email"
                placeholder="Email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                style={{ marginTop: "20px" }}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className={styles.error}>{formik.errors.email}</div>
              ) : null}
            </div>

            <div className={styles.phoneContainer}>
              <h3>Téléphone</h3>
              <InputCustom
                id="phoneNumber"
                type="tel"
                name="phoneNumber"
                placeholder="Numéro de téléphone"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phoneNumber}
                style={{ marginTop: "20px", marginBottom: "15px" }}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                <div className={styles.error}>{formik.errors.phoneNumber}</div>
              ) : null}
            </div>

            <div className={styles.btnContainer}>
              <Button
                disabled={formik.isSubmitting}
                type="submit"
                color={"var(--secondary-color)"}
                style={{
                  padding: "5px 10px",
                  marginLeft: "5px",
                  fontSize: "0.8rem",
                }}
              >
                Enregistrer
              </Button>
              <Button
                type="button"
                color="var(--secondary-color)"
                onClick={handleCancelCreatingClient}
                style={{
                  padding: "5px 10px",
                  marginLeft: "5px",
                  fontSize: "0.8rem",
                }}
              >
                Annuler
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className={styles.clientDisplay}>
          <h2 className={styles.title}>Liste des clients</h2>

          {clients && clients.length > 0 ? (
            <ClientTable clients={clients} setClients={setClients} />
          ) : (
            <p>Aucun client trouvé</p>
          )}

          <div className={styles.btnContainer}>
            <Button
              onClick={handleCreateNewClient}
              color="var(--secondary-color)"
            >
              + Créer un nouveau client
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
